import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";

const CHECKS_NL = [
  {
    key: "age",
    label: "Ik bevestig dat ik 18 jaar of ouder ben.",
  },
  {
    key: "research",
    label: "Ik verklaar dat de producten uitsluitend voor in-vitro onderzoeksdoeleinden worden gebruikt.",
  },
  {
    key: "consumption",
    label: "Ik erken dat deze producten NIET geschikt zijn voor menselijke of dierlijke consumptie.",
  },
  {
    key: "law",
    label: "Ik ben verantwoordelijk voor de naleving van lokale wetgeving met betrekking tot import, bezit en gebruik.",
  },
  {
    key: "terms",
    label: "Ik heb de onderzoeksdisclaimer en algemene voorwaarden gelezen en geaccepteerd.",
  },
];

const CHECKS_EN = [
  { key: "age", label: "I confirm that I am 18 years or older." },
  { key: "research", label: "I declare that the products will be used exclusively for in-vitro research purposes." },
  { key: "consumption", label: "I acknowledge that these products are NOT fit for human or animal consumption." },
  { key: "law", label: "I am responsible for compliance with local law regarding import, possession, and use." },
  { key: "terms", label: "I have read and accepted the research disclaimer and terms & conditions." },
];

/**
 * Renders required compliance checkboxes. Calls onChange with `true` when
 * all checkboxes are checked.
 */
export default function ComplianceCheckboxes({ onValidChange }) {
  const { lang } = useI18n();
  const nl = lang === "nl";
  const de = lang === "de";
  const requiredLabel = nl ? "VEREIST · VINK ALLES AAN" : de ? "ERFORDERLICH · ALLES ANKREUZEN" : "REQUIRED · TICK ALL";
  const allSignedLabel = nl ? "✓ ALLE AKKOORDEN GEGEVEN" : de ? "✓ ALLE ZUSTIMMUNGEN GEGEBEN" : "✓ ALL AGREEMENTS GIVEN";
  const CHECKS_DE = [
    { key: "age", label: "Ich bestätige, dass ich 18 Jahre oder älter bin." },
    { key: "research", label: "Ich erkläre, dass die Produkte ausschließlich für In-vitro-Forschungszwecke verwendet werden." },
    { key: "consumption", label: "Ich erkenne an, dass diese Produkte NICHT für den menschlichen oder tierischen Verzehr geeignet sind." },
    { key: "law", label: "Ich bin für die Einhaltung lokaler Gesetze bezüglich Einfuhr, Besitz und Verwendung verantwortlich." },
    { key: "terms", label: "Ich habe den Forschungsdisclaimer und die AGB gelesen und akzeptiert." },
  ];
  const items = nl ? CHECKS_NL : de ? CHECKS_DE : CHECKS_EN;
  const [state, setState] = useState(() =>
    Object.fromEntries(items.map((c) => [c.key, false]))
  );

  const allChecked = items.every((c) => state[c.key]);

  useEffect(() => {
    if (typeof onValidChange === "function") onValidChange(allChecked);
  }, [allChecked, onValidChange]);

  const toggle = (k) => setState((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div data-testid="compliance-checks" className="border border-neutral-300 bg-white p-6 md:p-8 mt-8">
      <div className="flex items-center gap-2 mb-5">
        <span className="font-mono-label text-[10px] tracking-[0.2em] text-[#D12A2F]">
          [ {requiredLabel} ]
        </span>
        <AnimatePresence>
          {allChecked && (
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0 }}
              className="font-mono-label text-[10px] text-emerald-700"
            >
              {allSignedLabel}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      <div className="space-y-3">
        {items.map((c) => (
          <label
            key={c.key}
            data-testid={`check-${c.key}`}
            className={`flex items-start gap-3 cursor-pointer group p-2 -mx-2 transition-colors ${
              state[c.key] ? "" : "hover:bg-neutral-50"
            }`}
          >
            <span
              className={`relative flex items-center justify-center w-5 h-5 mt-0.5 border transition-colors flex-shrink-0 ${
                state[c.key] ? "bg-black border-black" : "bg-white border-neutral-400 group-hover:border-black"
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={state[c.key]}
                onChange={() => toggle(c.key)}
              />
              {state[c.key] && (
                <motion.svg
                  initial={{ scale: 0, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.2 }}
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                >
                  <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </motion.svg>
              )}
            </span>
            <span className={`text-sm leading-relaxed ${state[c.key] ? "text-neutral-500" : "text-neutral-800"}`}>
              {c.label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
